import {
  PatientPhotoModification,
  PatientPhotoType,
  PhotosFileKindEnum,
} from '@data/models';
import { cn } from '@utils';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PreviewMode from './PreviewMode';
import styles from './styles.scss';
import { ImageState } from '../../model/types';
import { TransformedImage } from '../TransformedImage';

type PhotoProps = {
  fileKind: PhotosFileKindEnum;
  url: string;
  isViewPortPhotoEnabled: boolean;
  setSelectedPhoto?: React.Dispatch<
    React.SetStateAction<PatientPhotoType | undefined>
  >;
  selectedPhoto?: PatientPhotoType;
  onRadioChange?: () => void;
  modifications?: PatientPhotoModification;
};

const Photo = ({
  fileKind,
  url,
  isViewPortPhotoEnabled,
  selectedPhoto,
  setSelectedPhoto,
  onRadioChange,
  modifications,
}: PhotoProps) => {
  const [isPreviewModeOpen, setIsPreviewModeOpen] = useState<boolean>(false);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({
    width: 168,
    height: 149,
  });
  const { t } = useTranslation();

  let rotationAngleDegree = 0;

  if (modifications?.rotationAngleDegree) {
    rotationAngleDegree = modifications?.isFlipped
      ? modifications.rotationAngleDegree
      : -modifications.rotationAngleDegree;
  }

  const imageState: ImageState = {
    isFlipped: modifications?.isFlipped || false,
    rotate: 0,
    rotationAngleDegree,
    grid: false,
    zoom: 1,
    normalizedCropRect: {
      xmax: modifications?.normalizedCropRect?.xmax || 1,
      xmin: modifications?.normalizedCropRect?.xmin || 0,
      ymax: modifications?.normalizedCropRect?.ymax || 1,
      ymin: modifications?.normalizedCropRect?.ymin || 0,
    },
  };

  useEffect(() => {
    const img = document.createElement('img');
    img.src = url;
    img.onload = () => {
      setImage(img);
    };
    img.onerror = () => {
      console.error('Failed to load image.');
    };
  }, [url]);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const handlePhotoClick = () => {
    if (!isViewPortPhotoEnabled) {
      setIsPreviewModeOpen(true);
    }
  };

  const handlePreviewModeClose = () => {
    setIsPreviewModeOpen(false);
  };

  return (
    <>
      {isPreviewModeOpen && (
        <PreviewMode
          defaultImageState={imageState}
          isPreviewModeOpen={isPreviewModeOpen}
          onPreviewModeClose={handlePreviewModeClose}
          url={url}
          modifications={modifications}
        />
      )}
      <div>
        <div
          ref={containerRef}
          className={styles.patientPhoto}
          onClick={handlePhotoClick}
          onTouchEnd={handlePhotoClick}
        >
          <TransformedImage
            image={image}
            containerSize={containerSize}
            modifications={imageState}
            fitContainer
          />
        </div>
        <div className={styles.patientPhoto_footer}>
          <input
            id={fileKind}
            name="photo-group"
            type="radio"
            value={fileKind}
            checked={selectedPhoto?.fileKind === fileKind}
            onChange={(event) => {
              onRadioChange?.();
              setSelectedPhoto?.({
                fileKind: event.target.value as PhotosFileKindEnum,
                url: url,
              });
            }}
            className={cn(
              styles.patientPhoto_radio,
              isViewPortPhotoEnabled ? 'inline-block' : 'hidden'
            )}
          />
          <label className={styles.patientPhoto_label} htmlFor={fileKind}>
            {t(fileKind)}
          </label>
        </div>
      </div>
    </>
  );
};

export default Photo;
